import { defineStore } from 'pinia';
import { TokenService } from '@/services/token';
import { UserService, AuthenticationError } from '@/services/user';
import router from '@/router/router';
import { FeatureEnum, getFeaturesByRole, getRoleByFeatures, rolesTypes } from '@/utils/userRoles';
import { useBotsStore } from '@/stores/bots';
import { useLivechatStore } from '@/stores/livechat';
import { useIntentsStore } from '@/stores/intents';
import { DEV_LOGIN_OPT_IN_KEY } from '@/dev/devLoginOptIn';

/** State of the auth module */
export interface AuthState {
  /** Whether a login request is currently in flight */
  authenticating: boolean;
  /** Whether the last login attempt failed */
  authenticationError: boolean;
  /** Human-readable message for the last login error, '' if none */
  authenticationErrorMessage: string;
  /** Decoded JWT user object (with `features` array etc.), or `false` when logged out */
  user: any;
  /** Unique bot ID that was last active, or `null` */
  lastUsedBotId: string | null;

  /** Emulate another access role. '' when not emulating (persisted in sessionStorage as 'viewHubAs') */
  fauxRole: string;
  /** All roles you can emulate. Order matters. High to low. */
  accessRoles: string[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/auth.ts` (namespace `auth`).
 * Persisted to `localStorage` (full state) — matches the legacy `vuexLocal` reducer's
 * `auth: state.auth` entry in `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Pinia merges state and getters onto a single `this`, so any legacy state prop that also had a
 *   same-named getter is a REAL collision here (not just a Vuex-registry curiosity). Legacy had
 *   four such pairs — in every case the STATE PROP keeps its name (required: this module is
 *   persisted, so state key names are load-bearing for the `vuex` localStorage item) and the
 *   GETTER is renamed `<name>Getter`:
 *     - state `authenticating` + getter `authenticating` → getter `authenticatingGetter`
 *     - state `authenticationError` + getter `authenticationError` → getter `authenticationErrorGetter`
 *     - state `authenticationErrorMessage` + getter `authenticationErrorMessage` → getter `authenticationErrorMessageGetter`
 *     - state `user` + getter `user` → getter `userGetter` (NOT an identity passthrough — it
 *       overlays `fauxRole` permissions onto the raw decoded user — so all the other getters that
 *       used to read `getters.user` now read `this.userGetter`)
 *     - state `lastUsedBotId` + getter `lastUsedBotId` → getter `lastUsedBotIdGetter` (identity
 *       passthrough)
 * - Legacy action `loginReset` merely committed the mutation `loginReset` of the same name —
 *   merged into a single action `loginReset`.
 * - All other legacy mutations (`loginRequest`, `loginSuccess`, `loginError`, `logoutSuccess`,
 *   `setUser`, `setLastUsedBotId`, `pretendToBe`) become same-named actions (no other name
 *   collisions for these).
 * - Cross-namespace access, resolved via sibling stores (called INSIDE the action/getter body):
 *   `dispatch('bots/get', null, {root:true})` → `useBotsStore().get()`;
 *   `dispatch('livechat/goOffline', null, {root:true})` → `useLivechatStore().goOffline()`;
 *   `rootGetters['intents/fallbackFaqIntentEnabled']` → `useIntentsStore().fallbackFaqIntentEnabled`.
 */
export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    authenticating: false,
    authenticationError: false,
    authenticationErrorMessage: '',
    user: false,
    lastUsedBotId: null,

    // Emulate another access role
    fauxRole: window.sessionStorage.getItem('viewHubAs') || '',
    // All roles you can emulate. Order matters. High to low.
    accessRoles: [rolesTypes.ADMIN, rolesTypes.OWNER, rolesTypes.EDITOR, rolesTypes.VIEWER, rolesTypes.TESTER],
  }),
  getters: {
    loggedIn(): boolean {
      return !!TokenService.getToken();
    },

    /** Renamed from legacy identity-passthrough getter `authenticationError` (collides with state prop). */
    authenticationErrorGetter(): boolean {
      return this.authenticationError;
    },
    /** Renamed from legacy identity-passthrough getter `authenticationErrorMessage` (collides with state prop). */
    authenticationErrorMessageGetter(): string {
      return this.authenticationErrorMessage;
    },

    /** Renamed from legacy identity-passthrough getter `authenticating` (collides with state prop). */
    authenticatingGetter(): boolean {
      return this.authenticating;
    },

    /**
     * Renamed from legacy getter `user` (collides with state prop `user`). NOT an identity
     * passthrough: overlays the emulated `fauxRole`'s permissions onto the raw decoded user.
     */
    userGetter(): any {
      if (!this.fauxRole) return this.user;
      if (!this.user) return null;

      // De-reference the objects we will mutate a little
      const user = { ...this.user };

      // Give the permissions, and ensure it also gives the role
      const features = new Set(getFeaturesByRole(this.fauxRole));
      // `fauxRole` is a plain string at runtime (role name), same loose value legacy passed here untyped.
      features.add(this.fauxRole as FeatureEnum);

      user.features = Array.from(features);
      return user;
    },

    /**
     * Get which role the user is pretending to have, or null if not pretending
     */
    isPretendingToBe(): string | null {
      return this.fauxRole || null;
    },
    /**
     * Returns the role you actually are, in case you are pretending to be another role.
     */
    isActually(): string | null {
      return getRoleByFeatures(this.user?.features) || null;
    },
    /**
     * Lists the roles you can pretend to be, including the one you currently are
     */
    canPretendAs(): string[] {
      const realRole = getRoleByFeatures(this.user?.features);
      if (!realRole) return [];

      const roleIndex = this.accessRoles.indexOf(realRole);
      return this.accessRoles.slice(roleIndex);
    },

    isAdmin(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return this.userGetter.features.some((feature: string) => feature === FeatureEnum.ADMIN);
      }

      return false;
    },
    isOwner(): boolean {
      if (!this.userGetter?.features) return false;
      const role = getRoleByFeatures(this.userGetter?.features);
      return role === rolesTypes.OWNER;
    },
    isAssistant(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return this.userGetter.features.some((feature: string) => feature === rolesTypes.ASSISTANT);
      }

      return false;
    },
    isRAG(): boolean {
      const ragEnabled = useIntentsStore().fallbackFaqIntentEnabled;

      if (ragEnabled === true) {
        return this.userGetter.features.some((feature: string) => feature === rolesTypes.RAG || feature === rolesTypes.ADMIN || feature === rolesTypes.OWNER || feature === FeatureEnum.SIMPLE_EDIT);
      }

      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return this.userGetter.features.some((feature: string) => feature === rolesTypes.RAG || feature === rolesTypes.ADMIN);
      }

      return false;
    },
    isTester(): boolean {
      if (!this.userGetter?.features) return false;
      const role = getRoleByFeatures(this.userGetter?.features);
      return role === rolesTypes.TESTER;
    },
    isNLPOwner(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return this.userGetter.features.some((feature: string) => feature === FeatureEnum.NLP);
      }

      return false;
    },
    isFormView(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return (
          this.userGetter.features.some((feature: string) => feature === FeatureEnum.FORM_VIEW) ||
          this.isAdmin
        );
      }

      return false;
    },
    isWidgetEditorView(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return (
          this.userGetter.features.some((feature: string) => feature === FeatureEnum.WIDGET_EDITOR_VIEW) ||
          this.isAdmin
        );
      }

      return false;
    },
    isConversationalView(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return (
          this.userGetter.features.some(
            (feature: string) => feature === FeatureEnum.CONVERSATIONAL_VIEW
          ) || this.isAdmin
        );
      }

      return false;
    },
    isChatInsights(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return (
          this.userGetter.features.some((feature: string) => feature === FeatureEnum.CHAT_INSIGHTS) ||
          this.isAdmin
        );
      }

      return false;
    },
    isOnlyStaging(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return this.userGetter.features.some((feature: string) => feature === FeatureEnum.ONLY_STAGING);
      }

      return false;
    },

    isNoBeamer(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return this.userGetter.features.some((feature: string) => feature === FeatureEnum.NO_BEAMER);
      }

      return false;
    },

    isSimpleEdit(): boolean {
      if (this.isAdmin || this.isOwner) return true;

      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return this.userGetter.features.some((feature: string) => feature === FeatureEnum.SIMPLE_EDIT);
      }

      return false;
    },

    isChannelEdit(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return (
          this.userGetter.features.some((feature: string) => feature === FeatureEnum.CHANNEL_EDIT) ||
          this.isAdmin
        );
      }

      return false;
    },

    isSuggestion(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return this.userGetter.features.some((feature: string) => feature === FeatureEnum.SUGGESTION);
      }

      return false;
    },
    isNewIntent(): boolean {
      if (
        this.userGetter &&
        this.userGetter.features &&
        Array.isArray(this.userGetter.features)
      ) {
        return (
          this.userGetter.features.some((feature: string) => feature === FeatureEnum.NEW_INTENT) ||
          this.isAdmin
        );
      }

      return false;
    },
    isMultiBot(): boolean {
      return Array.isArray(this.userGetter?.features) && this.userGetter.features.includes(FeatureEnum.MULTIBOT);
    },
    isLivechat(): boolean {
      return Array.isArray(this.userGetter?.features) && this.userGetter.features.includes(FeatureEnum.LIVECHAT);
    },
    isSipgate(): boolean {
      return Array.isArray(this.userGetter?.features) && this.userGetter.features.includes(FeatureEnum.SIPGATE);
    },
    isSupervisor(): boolean {
      return Array.isArray(this.userGetter?.features) && this.userGetter.features.includes(FeatureEnum.SUPERVISOR);
    },

    /** Renamed from legacy identity-passthrough getter `lastUsedBotId` (collides with state prop). */
    lastUsedBotIdGetter(): string | null {
      return this.lastUsedBotId;
    },
  },
  actions: {
    /**
     * Merged legacy action `loginReset` (which merely committed the mutation of the same name) +
     * legacy mutation `loginReset`.
     */
    async loginReset() {
      this.authenticating = false;
      this.authenticationError = false;
      this.authenticationErrorMessage = '';
    },
    async login({ username, password }: { username: string; password: string }) {
      // login request and set state
      this.loginRequest();

      try {
        // UserService.login()'s return type is optional-typed, but legacy code assumed it always
        // resolves an object here (relying on the thrown AuthenticationError for the failure path).
        const { token, two_fa } = (await UserService.login(username, password))!;

        if (token && !two_fa) {
          this.loginSuccess();
          const userObject = UserService.decodeToken(token);
          this.setUser(userObject);
          // Redirect the user to the page he first tried to visit or to the home view
          // load bots and set first one as active
          useBotsStore().get().then(() => {
            router.push((router.currentRoute?.value?.query?.redirect || '/') as any);
          });
        } else {
          //2FA auth required
          this.loginSuccess();
          const redirectParam = router?.currentRoute?.value?.query?.redirect
            ? `?redirect=${router.currentRoute.value.query.redirect}`
            : '';
          router.push(`/2fa/${token}${redirectParam}`);
          return false;
        }

        return true;
      } catch (e: any) {
        if (e instanceof AuthenticationError) {
          this.loginError({ errorCode: e.errorCode, message: e.message });
        } else {
          this.loginError({ errorCode: 401 });
        }

        return false;
      }
    },
    async twofaLogin({ twofa_token, usertoken }: { twofa_token: string; usertoken: string }) {
      // login request and set state
      this.loginRequest();

      try {
        const token = await UserService.twofaLogin(twofa_token, usertoken);
        if (token) {
          this.loginSuccess();
          const userObject = UserService.decodeToken(token);
          this.setUser(userObject);
          useBotsStore().get().then(() => {
            // Redirect the user to the page he first tried to visit or to the home view
            router.push((router?.currentRoute?.value?.query?.redirect || '/') as any);
          });
        } else {
          this.loginError({ errorCode: 100 });
          return false;
        }

        return true;
      } catch (e: any) {
        if (e instanceof AuthenticationError) {
          this.loginError({ errorCode: e.errorCode });
        } else {
          this.loginError({ errorCode: 401 });
        }

        return false;
      }
    },
    async requestPasswordReset({ username }: { username: string }) {
      try {
        const response = UserService.requestPasswordReset(username);
        return response;
      } catch (e: any) {
        throw new Error(e);
      }
    },

    async setNewPassword({ username, password, token }: { username: string; password: string; token: string }) {
      // No catch here because we need the 400 error response in the component
      const response = await UserService.setNewPassword(
        username,
        password,
        token
      );
      return response;
    },
    async requestInvite({ email, bot }: { email: string; bot: string }) {
      // No catch here because we need the 400 error response in the component
      // UserService.requestInvite() now requires a 3rd `features` arg (added after this action was
      // written); legacy calls it the same way with only 2 args (latent pre-existing bug, present
      // identically in src/store/modules/auth.ts). Cast the fn ref to `any` to keep the exact same
      // runtime call rather than inventing a features value that was never actually passed.
      const response = await (UserService.requestInvite as any)(email, bot);
      return response;
    },
    async completeInvite({ username, password, token }: { username: string; password: string; token: string }) {
      // No catch here because we need the 400 error response in the component
      const response = await UserService.completeInvite(
        username,
        password,
        token
      );
      return response;
    },

    // @ payload { reload: boolean }
    /**
     * @param payload.byUser Logged out by hand, not because the session ended
     */
    logout(payload?: { reload?: boolean; byUser?: boolean }) {
      // Dev server only: logging out by hand ends the dev auto-login too (see src/dev/devAutoLogin.ts)
      if (import.meta.env.DEV && payload?.byUser) window.localStorage.removeItem(DEV_LOGIN_OPT_IN_KEY);

      UserService.logout();
      // call the livechat store
      useLivechatStore().goOffline();
      this.logoutSuccess();
      if (payload?.reload) {
        // Legacy `forceReload` argument (not in current DOM typings), kept for identical runtime behavior
        (window.location as any).reload(true);
      } else {
        // goto login page
        router.push('/login');
      }
    },

    async getUser({ isPublic = false }: { isPublic?: boolean } = {}) {
      try {
        // Hack for language switch: (user.language is not changed directly. Need logout/login. Only auth/me gives current language.)
        /*
          ! [TokenInvalidation]
          In Firefox it triggers Logout logic after aborting the request
          In Chrome it doesn't, for some reason.
          So we try multiple times to get user data before logging out the user
        */
        let data, attempts = 0;
        for (attempts = 0; attempts <= 3; attempts++) {
          ({ data } = await UserService.getUser());
          if (data && data !== null) break;
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        if (!isPublic && (data === null || !data)) {
          return this.logout();
        }

        /**
         * Read the token AFTER `/auth/me` resolves, not before.
         *
         * During SSO login this action starts while the callback POST is still in
         * flight, so there is no token yet — the retry loop above is what gives the
         * callback time to store one. Decoding up front captured `null` and then
         * threw on `userObject.language` below, which the catch turned into a logout.
         */
        const userObject = UserService.decodeToken(TokenService.getToken());

        if (!userObject) {
          if (isPublic) return true;
          return this.logout();
        }

        if (data?.language) {
          userObject.language = data.language;
        }
        if (data?.features) {
          userObject.features = data.features;
        }
        this.setUser(userObject);

        return true;
      } catch (e) {
        if (isPublic) return true;
        console.error(e);

        return this.logout();
      }
    },

    async saveLanguage(language: string) {
      await UserService.saveLanguage({ username: this.user.username, language });
      this.getUser();
      return true;
    },

    /**
     * @typedef {Object} SSOCallbackData
     * @property {string} provider The identity provider, e.g. "microsoft"
     *
     * @property {Object} data SSO login specific data
     * @property {string} nonce Randomly generated string from before user started login.
     * The `nonce` when we redeem the `code` should match this value.
     * @property {string} code The code from the ID provider to obtain user data
     * @property {string} redirect_uri The URI the user was redirected to after login, to verify
     * @property {state} state The state from the ID provider, to verify
     * @property {?string} [invite] Optional: The invite code the user is trying to use.
     * If not present, user must already be a user.
     * Must be present and valid in order to generate a new user record.
     *
     * @property {Object} options Other additional info
     * @property {?string} [options.redirect] After successful login && auto-login, which dashboard path do you want to redirect user to?
     * @property {?boolean} [options.autoLogin] Stores token and preps the ApiService to use auth
     */
    /**
     * Handle OAuth SSO Callback
     * @param provider Name of the ID provider, e.g. "microsoft"
     * @param data Data from the callback URL, put into our query strings
     * @param options Options for the login (default `{redirect: true, autoLogin: true}`)
     */
    async handleSSOCallback(
      { provider, data, options }: { provider: string; data: any; options: { redirect?: boolean; autoLogin?: boolean } }
    ): Promise<{ success?: boolean; sucess?: boolean; error?: string }> {
      Object.assign({
        autoLogin: true,
        redirect: true,
      }, options);

      this.loginRequest();

      try {
        const result = await UserService.handleSSOCallback(provider, data, options);
        if (result.error) throw new Error(result.error);

        // 2FA is required
        if (result.two_fa) {
          this.loginSuccess();
          const redirectParam = router?.currentRoute?.value?.query?.redirect
            ? `?redirect=${router.currentRoute.value.query.redirect}`
            : '';

          router.push(`/2fa/${result.token}${redirectParam}`);
          return {sucess: true};
        }

        // 2FA is not required
        this.loginSuccess();
        const userObject = UserService.decodeToken(result.token);
        this.setUser(userObject);
        // Redirect the user to the page he first tried to visit or to the home view
        // load bots and set first one as active
        useBotsStore().get()
          .then(() => {
            if (options.redirect && options.autoLogin) {
              router.push((router?.currentRoute?.value?.query?.redirect || '/') as any);
            }
          });

        return {success: true};
      } catch(err: any) {
        return {
          success: false,
          error: err.message ?? err.toString(),
        };
      }
    },

    loginRequest() {
      // login is requested
      this.authenticating = true;
      this.authenticationError = false;
      this.authenticationErrorMessage = '';
    },

    loginSuccess() {
      // login was successfull
      this.authenticating = false;
    },

    loginError({ errorCode, message }: { errorCode: number; message?: string }) {
      // login had an error
      this.authenticating = false;
      this.authenticationError = errorCode === 401 ? true : true;
      this.authenticationErrorMessage = message || '';
    },

    logoutSuccess() {
      // logout
      this.user = false;
    },

    setUser(user: any) {
      this.user = user;
    },

    setLastUsedBotId(botId: string | null) {
      this.lastUsedBotId = botId;
    },

    /**
     * Change which hub role you are pretending to be or clear it
     * @param role Null to clear or the role you want to pretend to be.
     */
    pretendToBe(role: string | null = null) {
      const actualRole = getRoleByFeatures(this.user?.features);
      const actualIndex = this.accessRoles.indexOf(actualRole as string);
      // getRoleByFeatures() can return undefined; legacy assigns it straight into `role`
      // (string | null) untyped, so mirror that with a cast rather than a runtime guard.
      if (!role) role = actualRole as string | null;
      const targetIndex = this.accessRoles.indexOf(role as string);

      // Ensure target role exist
      if (targetIndex === -1) {
        console.error(`Cannot pretend to be role "${role}" because it does not exist.`);
        return;
      }

      // You cannot pretend "upwards" in the role hierarchy
      if (targetIndex < actualIndex) {
        console.error(`Cannot pretend to be role "${role}" because it is higher than your actual role.`);
        return;
      }

      // Simply remove the pretend value
      if (actualRole === role) role = '';

      // Set the new role to pretend to be
      this.fauxRole = role as string;
      window.sessionStorage.setItem('viewHubAs', role as string);
    },
  },
  vuexPersist: {
    local: true,
  },
});
